import {
  CLAIM_CHECK_KIND_LABEL,
  DOCUMENT_FIELD_RULE_LABEL,
  DOCUMENT_FIELD_RULES,
  type ClaimCheckDefInput,
  type ClaimCheckKind,
  type DocumentFieldRule,
} from '@cyberscore/contracts';
import { Plus, Trash2 } from 'lucide-react';
import type { DocumentTypeInfo } from './api';

const MAX = 5;

const blank = (kind: ClaimCheckKind, types: DocumentTypeInfo[]): ClaimCheckDefInput => {
  if (kind === 'policy') return { kind, document_types: [], statement: '' };
  if (kind !== 'document_field') return { kind, document_types: [] };
  const type = types.find((t) => t.fields.length) ?? types[0];
  return { kind, document_type: type?.key ?? '', field: type?.fields[0]?.key ?? '', rule: 'present' };
};

/**
 * Claim checks on a question (task 62): once answered, the answer is checked against the business's
 * documents. The agent reads evidence and policy checks; field checks use what extraction read.
 */
export function ChecksEditor({
  value,
  onChange,
  types,
  disabled,
}: {
  value: ClaimCheckDefInput[];
  onChange: (v: ClaimCheckDefInput[] | undefined) => void;
  types: DocumentTypeInfo[];
  disabled: boolean;
}) {
  const set = (i: number, c: ClaimCheckDefInput) => onChange(value.map((x, j) => (j === i ? c : x)));
  const remove = (i: number) => {
    const next = value.filter((_, j) => j !== i);
    onChange(next.length ? next : undefined);
  };
  return (
    <div className="stack-sm">
      {value.length === 0 && <p className="muted small">No checks: the answer counts as the business gives it.</p>}
      <ol className="option-rows">
        {value.map((c, i) => (
          <li key={i} className="option-row stack-sm">
            <div className="row gap">
              <select className="input" aria-label={`Check ${i + 1} kind`} disabled={disabled} value={c.kind} onChange={(e) => set(i, blank(e.target.value as ClaimCheckKind, types))}>
                {(Object.keys(CLAIM_CHECK_KIND_LABEL) as ClaimCheckKind[]).map((k) => (
                  <option key={k} value={k}>
                    {CLAIM_CHECK_KIND_LABEL[k]}
                  </option>
                ))}
              </select>
              <span className="grow" />
              {!disabled && (
                <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Remove check ${i + 1}`} onClick={() => remove(i)}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
            {c.kind === 'policy' && (
              <div className="field">
                <label htmlFor={`chk-${i}-statement`}>What the policy must say</label>
                <input id={`chk-${i}-statement`} className="input" disabled={disabled} value={c.statement} placeholder="Multi-factor authentication is required for every email account" onChange={(e) => set(i, { ...c, statement: e.target.value })} />
              </div>
            )}
            {c.kind !== 'document_field' ? (
              <div className="field">
                <label htmlFor={`chk-${i}-types`}>Only from these document types (none: any document)</label>
                <select id={`chk-${i}-types`} className="input" multiple size={Math.min(5, Math.max(2, types.length))} disabled={disabled} value={c.document_types ?? []} onChange={(e) => set(i, { ...c, document_types: [...e.target.selectedOptions].map((o) => o.value) })}>
                  {types.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <FieldCheck c={c} types={types} disabled={disabled} n={i + 1} onChange={(x) => set(i, x)} />
            )}
          </li>
        ))}
      </ol>
      {!disabled && value.length < MAX && (
        <button type="button" className="btn btn-sm" onClick={() => onChange([...value, blank('evidence', types)])}>
          <Plus size={14} /> Add a check
        </button>
      )}
    </div>
  ); // prettier-ignore
}

function FieldCheck({
  c,
  types,
  disabled,
  n,
  onChange,
}: {
  c: Extract<ClaimCheckDefInput, { kind: 'document_field' }>;
  types: DocumentTypeInfo[];
  disabled: boolean;
  n: number;
  onChange: (c: ClaimCheckDefInput) => void;
}) {
  const type = types.find((t) => t.key === c.document_type);
  const field = type?.fields.find((f) => f.key === c.field);
  const rules = DOCUMENT_FIELD_RULES.filter((r) => r !== 'not_expired' || !field || field.type === 'date');
  return (
    <div className="three">
      <div className="field">
        <label htmlFor={`chk-${n}-type`}>Document type</label>
        <select id={`chk-${n}-type`} className="input" disabled={disabled} value={c.document_type} onChange={(e) => onChange({ ...c, document_type: e.target.value, field: types.find((t) => t.key === e.target.value)?.fields[0]?.key ?? '' })}>
          {types.map((t) => (
            <option key={t.key} value={t.key}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor={`chk-${n}-field`}>Field</label>
        <select id={`chk-${n}-field`} className="input" disabled={disabled} value={c.field} onChange={(e) => { const date = type?.fields.find((f) => f.key === e.target.value)?.type === 'date'; onChange({ ...c, field: e.target.value, rule: c.rule === 'not_expired' && !date ? 'present' : c.rule }); }}>
          {(type?.fields ?? []).map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor={`chk-${n}-rule`}>Passes when it</label>
        <select id={`chk-${n}-rule`} className="input" disabled={disabled} value={c.rule} onChange={(e) => onChange({ ...c, rule: e.target.value as DocumentFieldRule })}>
          {rules.map((r) => (
            <option key={r} value={r}>
              {DOCUMENT_FIELD_RULE_LABEL[r]}
            </option>
          ))}
        </select>
      </div>
    </div>
  ); // prettier-ignore
}
